import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import SkillCategory from './components/SkillCategory';
import SkillsInAction from './components/SkillsInAction';
import CertificationsBadges from './components/CertificationsBadges';
import LearningRoadmap from './components/LearningRoadmap';
import PeerEndorsements from './components/PeerEndorsements';

const SkillsTechnicalMasteryShowcase = () => {
  const [activeCategory, setActiveCategory] = useState('Frontend');
  const [activeSection, setActiveSection] = useState('overview');

  const skillsData = {
    'Languages': [
      {
        name: "JavaScript",
        proficiency: 95,
        experience: 5,
        recentProjects: ["All Web Projects", "Mobile Apps", "Backend Services"],
        keyStrengths: ["ES6+", "Async/Await", "Promises", "Functional Programming"],
        codeSnippet: `const fetchUserData = async (userId) => {\n  try {\n    const { data } = await api.get(\`/users/\${userId}\`);\n    return data;\n  } catch (error) {\n    throw new Error(\`Failed to fetch user: \${error.message}\`);\n  }\n};`
      },
      {
        name: "Python",
        proficiency: 90,
        experience: 4,
        recentProjects: ["Machine Learning", "Data Processing", "Backend APIs"],
        keyStrengths: ["Flask", "Django", "TensorFlow", "Data Analysis"],
        codeSnippet: `from fastapi import FastAPI\nfrom pydantic import BaseModel\n\napp = FastAPI()\n\n@app.post("/predict")\nasync def predict(data: PredictionRequest):\n    return {"prediction": model.predict(data.features)}`
      },
      {
        name: "TypeScript",
        proficiency: 85,
        experience: 3,
        recentProjects: ["EduLift Platform", "Enterprise Applications"],
        keyStrengths: ["Type Safety", "Interfaces", "Generics", "Advanced Types"],
        codeSnippet: `interface ApiResponse<T> {\n  data: T;\n  status: 'success' | 'error';\n  message?: string;\n}`
      },
      {
        name: "C#",
        proficiency: 82,
        experience: 3,
        recentProjects: ["Blind Supporter", "Food Delivery System", "Garbage Collection"],
        keyStrengths: [".NET Core", "ASP.NET", "Entity Framework", "Web APIs"],
        codeSnippet: `public class UserController : Controller\n{\n    [HttpGet]\n    public async Task<IActionResult> GetUser(int id)\n    {\n        var user = await _userService.GetByIdAsync(id);\n        return Ok(user);\n    }\n}`
      },
      {
        name: "Java",
        proficiency: 80,
        experience: 3,
        recentProjects: ["Ceylon Cinema", "Spring Boot Apps"],
        keyStrengths: ["Spring Boot", "Servlets", "JSP", "Enterprise Apps"],
        codeSnippet: `@RestController\n@RequestMapping("/api/users")\npublic class UserController {\n    @GetMapping("/{id}")\n    public ResponseEntity<User> getUser(@PathVariable Long id) {\n        return ResponseEntity.ok(userService.findById(id));\n    }\n}`
      },
      {
        name: "Dart",
        proficiency: 88,
        experience: 3,
        recentProjects: ["SmartRecipe-AI", "AutoFix-LK", "BarberGo", "ToyMingle"],
        keyStrengths: ["Flutter", "Mobile Development", "State Management", "UI/UX"],
        codeSnippet: `class UserProfile extends StatelessWidget {\n  final User user;\n  UserProfile({required this.user});\n  \n  @override\n  Widget build(BuildContext context) {\n    return Card(child: Text(user.name));\n  }\n}`
      },
      {
        name: "PHP",
        proficiency: 85,
        experience: 4,
        recentProjects: ["POS System", "E-Commerce", "CMS Projects"],
        keyStrengths: ["Laravel", "RESTful APIs", "Database Integration", "Server-Side"],
        codeSnippet: `<?php\nRoute::get('/users/{id}', function ($id) {\n    return User::findOrFail($id);\n});\n?>`
      }
    ],
    'Frontend': [
      {
        name: "React.js",
        proficiency: 95,
        experience: 5,
        recentProjects: ["Job Portal", "Recruitment App", "EduLift", "POS System"],
        keyStrengths: ["Hooks", "Context API", "Component Design", "Performance"],
        codeSnippet: `const useOptimizedState = (initialValue) => {\n  const [state, setState] = useState(initialValue);\n  const memoizedValue = useMemo(() => state, [state]);\n  return [memoizedValue, setState];\n};`
      },
      {
        name: "Next.js",
        proficiency: 92,
        experience: 3,
        recentProjects: ["EduLift Platform"],
        keyStrengths: ["SSR/SSG", "API Routes", "File Routing", "Performance"],
        codeSnippet: `export async function getStaticProps() {\n  const data = await fetchData();\n  return { props: { data }, revalidate: 60 };\n}`
      },
      {
        name: "Tailwind CSS",
        proficiency: 90,
        experience: 3,
        recentProjects: ["Multiple Projects", "Component Libraries"],
        keyStrengths: ["Utility-First", "Responsive Design", "Custom Themes", "Dark Mode"],
        codeSnippet: `@layer components {\n  .btn-primary {\n    @apply px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600;\n  }\n}`
      },
      {
        name: "Express.js",
        proficiency: 92,
        experience: 4,
        recentProjects: ["Backend APIs", "Authentication Systems"],
        keyStrengths: ["Middleware", "RESTful APIs", "Error Handling", "Security"],
        codeSnippet: `const authMiddleware = (req, res, next) => {\n  const token = req.headers.authorization?.split(' ')[1];\n  if (!token) return res.status(401).json({ error: 'Unauthorized' });\n  next();\n};`
      },
      {
        name: "Node.js",
        proficiency: 93,
        experience: 4,
        recentProjects: ["Job Portal", "Real-time Apps", "Microservices"],
        keyStrengths: ["Async Programming", "Event Loop", "Streams", "NPM"],
        codeSnippet: `const express = require('express');\nconst app = express();\n\napp.use(express.json());\napp.use('/api', apiRoutes);\n\napp.listen(3000, () => console.log('Server running'));`
      },
      {
        name: "Flutter",
        proficiency: 90,
        experience: 3,
        recentProjects: ["SmartRecipe-AI", "AutoFix-LK", "BarberGo", "Food Ordering App"],
        keyStrengths: ["Cross-Platform", "Material Design", "State Management", "Firebase"],
        codeSnippet: `class MyApp extends StatelessWidget {\n  @override\n  Widget build(BuildContext context) {\n    return MaterialApp(\n      home: Scaffold(appBar: AppBar(title: Text('App')))\n    );\n  }\n}`
      }
    ],
    'Backend': [
      {
        name: "Flask",
        proficiency: 90,
        experience: 4,
        recentProjects: ["Recruitment App", "X-ray Detection", "Job Portal"],
        keyStrengths: ["REST APIs", "JWT Authentication", "Database Integration", "Werkzeug"],
        codeSnippet: `from flask import Flask, jsonify\nfrom flask_jwt_extended import JWTManager\n\napp = Flask(__name__)\napp.config['JWT_SECRET_KEY'] = 'secret'\njwt = JWTManager(app)\n\n@app.route('/api/users')\ndef get_users():\n    return jsonify(users)`
      },
      {
        name: "Django",
        proficiency: 85,
        experience: 3,
        recentProjects: ["X-ray Detection System", "Backend Services"],
        keyStrengths: ["ORM", "Admin Panel", "Authentication", "RESTful APIs"],
        codeSnippet: `from django.db import models\n\nclass User(models.Model):\n    name = models.CharField(max_length=100)\n    email = models.EmailField(unique=True)\n    created_at = models.DateTimeField(auto_now_add=True)`
      },
      {
        name: "Spring Boot",
        proficiency: 82,
        experience: 3,
        recentProjects: ["Job Portal System", "Enterprise Apps"],
        keyStrengths: ["Microservices", "RESTful APIs", "Security", "JPA"],
        codeSnippet: `@RestController\n@RequestMapping("/api")\npublic class ApiController {\n    @Autowired\n    private UserService userService;\n    \n    @GetMapping("/users")\n    public ResponseEntity<List<User>> getUsers() {\n        return ResponseEntity.ok(userService.findAll());\n    }\n}`
      },
      {
        name: "ASP.NET Core",
        proficiency: 83,
        experience: 3,
        recentProjects: ["Blind Supporter", "Food Delivery System"],
        keyStrengths: [".NET Core", "Web APIs", "Entity Framework", "Authentication"],
        codeSnippet: `[ApiController]\n[Route("api/[controller]")]\npublic class UsersController : ControllerBase\n{\n    [HttpGet]\n    public async Task<ActionResult<IEnumerable<User>>> GetUsers()\n    {\n        return await _context.Users.ToListAsync();\n    }\n}`
      },
      {
        name: "TensorFlow",
        proficiency: 85,
        experience: 3,
        recentProjects: ["X-ray Detection", "SmartRecipe-AI", "EduLift"],
        keyStrengths: ["CNN Models", "Image Processing", "Model Training", "Keras API"],
        codeSnippet: `import tensorflow as tf\nfrom tensorflow import keras\n\nmodel = keras.Sequential([\n    keras.layers.Conv2D(32, 3, activation='relu'),\n    keras.layers.MaxPooling2D(),\n    keras.layers.Flatten(),\n    keras.layers.Dense(10, activation='softmax')\n])`
      },
      {
        name: "Hibernate",
        proficiency: 80,
        experience: 2,
        recentProjects: ["Java Applications", "Database ORM"],
        keyStrengths: ["ORM", "Caching", "Lazy Loading", "Transactions"],
        codeSnippet: `@Entity\n@Table(name = "users")\npublic class User {\n    @Id\n    @GeneratedValue(strategy = GenerationType.IDENTITY)\n    private Long id;\n    \n    @Column(nullable = false)\n    private String name;\n}`
      }
    ],
    'Database': [
      {
        name: "MySQL",
        proficiency: 92,
        experience: 5,
        recentProjects: ["SLIBTEC Inventory", "Job Portal", "Ceylon Cinema", "POS System"],
        keyStrengths: ["Complex Queries", "Indexing", "Stored Procedures", "Optimization"],
        codeSnippet: `SELECT u.name, COUNT(o.id) as order_count\nFROM users u\nLEFT JOIN orders o ON u.id = o.user_id\nWHERE u.status = 'active'\nGROUP BY u.id\nHAVING order_count > 5;`
      },
      {
        name: "MongoDB",
        proficiency: 85,
        experience: 3,
        recentProjects: ["Real-time Apps", "NoSQL Projects"],
        keyStrengths: ["Aggregation", "Schema Design", "Indexing", "Replication"],
        codeSnippet: `db.users.aggregate([\n  { $match: { status: 'active' } },\n  { $group: { _id: '$department', count: { $sum: 1 } } },\n  { $sort: { count: -1 } }\n]);`
      },
      {
        name: "Firebase Firestore",
        proficiency: 88,
        experience: 3,
        recentProjects: ["SmartRecipe-AI", "AutoFix-LK", "BarberGo", "ToyMingle"],
        keyStrengths: ["Real-time Sync", "Authentication", "Cloud Storage", "NoSQL"],
        codeSnippet: `import { collection, addDoc } from 'firebase/firestore';\n\nconst docRef = await addDoc(collection(db, 'users'), {\n  name: 'John Doe',\n  email: 'john@example.com',\n  createdAt: new Date()\n});`
      },
      {
        name: "PostgreSQL",
        proficiency: 80,
        experience: 2,
        recentProjects: ["Job Portal", "Analytics Systems"],
        keyStrengths: ["Advanced Queries", "JSON Support", "Transactions", "ACID"],
        codeSnippet: `WITH monthly_sales AS (\n  SELECT DATE_TRUNC('month', created_at) as month,\n         SUM(amount) as total\n  FROM orders\n  GROUP BY month\n)\nSELECT * FROM monthly_sales ORDER BY month;`
      },
      {
        name: "Apache Kafka",
        proficiency: 75,
        experience: 2,
        recentProjects: ["Job Portal", "Real-time Updates"],
        keyStrengths: ["Message Streaming", "Event Processing", "Scalability", "Real-time"],
        codeSnippet: `const kafka = require('kafka-node');\nconst client = new kafka.KafkaClient();\nconst producer = new kafka.Producer(client);\n\nproducer.send([{\n  topic: 'updates',\n  messages: 'New job posted'\n}], callback);`
      },
      {
        name: "SQLAlchemy",
        proficiency: 82,
        experience: 3,
        recentProjects: ["Python Backend", "Flask Apps"],
        keyStrengths: ["ORM", "Query Builder", "Migrations", "Relationships"],
        codeSnippet: `from sqlalchemy import create_engine, Column, Integer, String\nfrom sqlalchemy.ext.declarative import declarative_base\n\nBase = declarative_base()\n\nclass User(Base):\n    __tablename__ = 'users'\n    id = Column(Integer, primary_key=True)\n    name = Column(String(50))`
      }
    ],
    'DevOps': [
      {
        name: "Docker",
        proficiency: 85,
        experience: 3,
        recentProjects: ["Job Portal Deployment", "Containerization"],
        keyStrengths: ["Containerization", "Docker Compose", "Multi-stage Builds", "Deployment"],
        codeSnippet: `FROM node:18-alpine AS builder\nWORKDIR /app\nCOPY package*.json ./\nRUN npm ci --only=production\n\nFROM node:18-alpine\nWORKDIR /app\nCOPY --from=builder /app/node_modules ./node_modules\nCOPY . .\nEXPOSE 3000\nCMD ["npm", "start"]`
      },
      {
        name: "AWS",
        proficiency: 82,
        experience: 3,
        recentProjects: ["Recruitment App", "Cloud Deployment"],
        keyStrengths: ["EC2", "S3", "Lambda", "Cloud Deployment", "Scalability"],
        codeSnippet: `const AWS = require('aws-sdk');\nconst s3 = new AWS.S3();\n\nconst uploadFile = async (file, bucket, key) => {\n  const params = { Bucket: bucket, Key: key, Body: file };\n  return await s3.upload(params).promise();\n};`
      },
      {
        name: "Git",
        proficiency: 95,
        experience: 5,
        recentProjects: ["All Projects", "Version Control", "Collaboration"],
        keyStrengths: ["Branching", "Code Review", "Collaboration", "CI/CD"],
        codeSnippet: `git checkout -b feature/new-component\ngit add .\ngit commit -m "feat: add new component with tests"\ngit push origin feature/new-component`
      },
      {
        name: "GitHub",
        proficiency: 92,
        experience: 5,
        recentProjects: ["All Projects", "Code Hosting", "Collaboration"],
        keyStrengths: ["Repository Management", "Pull Requests", "Actions", "Collaboration"],
        codeSnippet: `name: Deploy\non:\n  push:\n    branches: [main]\njobs:\n  deploy:\n    runs-on: ubuntu-latest\n    steps:\n      - uses: actions/checkout@v3\n      - run: npm ci\n      - run: npm test`
      }
    ],
    'Tools': [
      {
        name: "MSSQL",
        proficiency: 85,
        experience: 3,
        recentProjects: ["SLIBTEC Inventory", "Enterprise Apps"],
        keyStrengths: ["T-SQL", "Stored Procedures", "Query Optimization", "Database Design"],
        codeSnippet: `CREATE PROCEDURE GetActiveUsers\nAS\nBEGIN\n    SELECT * FROM Users\n    WHERE Status = 'Active'\n    ORDER BY CreatedDate DESC;\nEND;`
      },
      {
        name: "Postman",
        proficiency: 90,
        experience: 4,
        recentProjects: ["API Testing", "Documentation", "All Backend Projects"],
        keyStrengths: ["API Testing", "Collections", "Automation", "Documentation"],
        codeSnippet: `pm.test("Status code is 200", function () {\n  pm.response.to.have.status(200);\n});\n\npm.test("Response has user data", function () {\n  const jsonData = pm.response.json();\n  pm.expect(jsonData).to.have.property('user');\n});`
      },
      {
        name: "Git",
        proficiency: 95,
        experience: 5,
        recentProjects: ["All Projects", "Version Control"],
        keyStrengths: ["Branching", "Merging", "Conflict Resolution", "Best Practices"],
        codeSnippet: `git checkout -b feature/new-feature\ngit add .\ngit commit -m "feat: implement new feature"\ngit push origin feature/new-feature`
      },
      {
        name: "AWS",
        proficiency: 82,
        experience: 3,
        recentProjects: ["Cloud Deployments", "File Storage"],
        keyStrengths: ["EC2", "S3", "Lambda", "Deployment"],
        codeSnippet: `const s3 = new AWS.S3();\nconst params = {\n  Bucket: 'my-bucket',\n  Key: 'file.jpg',\n  Body: fileBuffer\n};\ns3.upload(params, callback);`
      },
      {
        name: "Matplotlib",
        proficiency: 80,
        experience: 3,
        recentProjects: ["Data Visualization", "Analytics"],
        keyStrengths: ["Data Visualization", "Charts", "Plotting", "Analytics"],
        codeSnippet: `import matplotlib.pyplot as plt\n\nplt.figure(figsize=(10, 6))\nplt.plot(x_data, y_data, marker='o')\nplt.title('Data Analysis')\nplt.xlabel('X Axis')\nplt.ylabel('Y Axis')\nplt.show()`
      },
      {
        name: "Plotly",
        proficiency: 78,
        experience: 2,
        recentProjects: ["Interactive Charts", "Dashboards"],
        keyStrengths: ["Interactive Viz", "Dashboards", "Real-time Charts", "Web Integration"],
        codeSnippet: `import plotly.express as px\n\nfig = px.line(df, x='date', y='value', title='Interactive Chart')\nfig.show()`
      },
      {
        name: "Streamlit",
        proficiency: 75,
        experience: 2,
        recentProjects: ["ML Dashboards", "Data Apps"],
        keyStrengths: ["Data Apps", "ML Visualization", "Rapid Prototyping", "Interactive UI"],
        codeSnippet: `import streamlit as st\n\nst.title('Data Dashboard')\nst.write('Welcome to the dashboard')\nst.line_chart(data)`
      },
      {
        name: "Kafka",
        proficiency: 75,
        experience: 2,
        recentProjects: ["Job Portal", "Event Streaming"],
        keyStrengths: ["Message Streaming", "Real-time Processing", "Event-driven", "Scalability"],
        codeSnippet: `const { Kafka } = require('kafkajs');\nconst kafka = new Kafka({ brokers: ['localhost:9092'] });\nconst producer = kafka.producer();\nawait producer.send({\n  topic: 'events',\n  messages: [{ value: 'Hello' }]\n});`
      }
    ]
  };

  const sections = [
    { id: 'overview', name: 'Skills Overview', icon: 'BarChart3' },
    { id: 'skills-in-action', name: 'Skills in Action', icon: 'Zap' },
    { id: 'certifications', name: 'Certifications', icon: 'Award' },
    { id: 'learning-roadmap', name: 'Learning Roadmap', icon: 'Map' },
    { id: 'endorsements', name: 'Peer Endorsements', icon: 'Users' }
  ];

  const categories = Object.keys(skillsData);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'skills-in-action', 'certifications', 'learning-roadmap', 'endorsements'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Skills - Technical Mastery Showcase | CodeShow LapZ</title>
        <meta name="description" content="Explore CodeShow LapZ's comprehensive technical expertise across frontend, backend, database, DevOps, and tools. Interactive demonstrations, certifications, and peer endorsements showcase 4+ years of full-stack development excellence." />
        <meta name="keywords" content="React, Node.js, JavaScript, TypeScript, Python, AWS, MongoDB, PostgreSQL, Docker, Full Stack Developer, Technical Skills, Certifications" />
        <meta property="og:title" content="Technical Skills & Expertise - CodeShow LapZ" />
        <meta property="og:description" content="Interactive showcase of technical mastery across modern web technologies, cloud platforms, and development tools with real project applications." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/skills-technical-mastery-showcase" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-br from-primary/5 via-brand-purple/5 to-accent/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium border border-primary/20 mb-6">
                <Icon name="Zap" size={16} />
                <span>Technical Expertise</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
                Technical Mastery
                <span className="block gradient-text">Showcase</span>
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                Explore my comprehensive technical expertise across the full development stack. 
                From interactive demonstrations to real-world applications, discover how I transform 
                complex problems into elegant solutions.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2 shadow-premium">
                  <Icon name="Code" size={20} className="text-primary" />
                  <span className="font-semibold text-text-primary">25+ Technologies</span>
                </div>
                <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2 shadow-premium">
                  <Icon name="Award" size={20} className="text-success" />
                  <span className="font-semibold text-text-primary">6 Certifications</span>
                </div>
                <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2 shadow-premium">
                  <Icon name="Users" size={20} className="text-brand-purple" />
                  <span className="font-semibold text-text-primary">15+ Endorsements</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Sidebar */}
        <div className="fixed left-6 top-1/2 transform -translate-y-1/2 z-40 hidden xl:block">
          <div className="bg-white rounded-xl shadow-premium border border-border p-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-premium mb-1 ${
                  activeSection === section.id
                    ? 'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                }`}
                title={section.name}
              >
                <Icon name={section.icon} size={18} />
                <span className="hidden 2xl:block">{section.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 space-y-24">
          {/* Skills Overview Section */}
          <section id="overview" className="space-y-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-text-primary mb-4">Skills Overview</h2>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                Interactive exploration of my technical expertise across different categories. 
                Click on each category to see detailed proficiency levels, experience, and code samples.
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-3 rounded-lg font-medium transition-premium ${
                    activeCategory === category
                      ? 'bg-primary text-white shadow-premium'
                      : 'bg-surface text-text-secondary hover:bg-surface-hover hover:text-text-primary border border-border'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Skills Categories */}
            <div className="space-y-6">
              {categories.map((category) => (
                <SkillCategory
                  key={category}
                  category={category}
                  skills={skillsData[category]}
                  isActive={activeCategory === category}
                  onToggle={() => setActiveCategory(activeCategory === category ? '' : category)}
                />
              ))}
            </div>
          </section>

          {/* Skills in Action Section */}
          <section id="skills-in-action">
            <SkillsInAction />
          </section>

          {/* Certifications Section */}
          <section id="certifications">
            <CertificationsBadges />
          </section>

          {/* Learning Roadmap Section */}
          <section id="learning-roadmap">
            <LearningRoadmap />
          </section>

          {/* Peer Endorsements Section */}
          <section id="endorsements">
            <PeerEndorsements />
          </section>
        </div>

        {/* Mobile Navigation */}
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 xl:hidden z-40">
          <div className="bg-white rounded-full shadow-premium border border-border p-2 flex space-x-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-premium ${
                  activeSection === section.id
                    ? 'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                }`}
                title={section.name}
              >
                <Icon name={section.icon} size={20} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SkillsTechnicalMasteryShowcase;